import { useState, useRef } from 'react'
import { X, Upload, Download, CheckCircle, AlertCircle, FileText, ChevronDown, ChevronUp } from 'lucide-react'
import { parseCSV, validateAndTransformRow, triggerCSVDownload, CSV_TEMPLATE } from '../../utils/csvHelpers'

function RowCard({ row, index }) {
  const [expanded, setExpanded] = useState(false)
  const isValid = row.errors.length === 0

  return (
    <div className={`rounded-xl border p-3 text-sm ${isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-2 min-w-0">
          {isValid
            ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            : <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          }
          <div className="min-w-0">
            {isValid ? (
              <p className="font-medium text-green-800 truncate">
                {row.entry.date} &bull; {row.meta.branchName} &bull; {row.meta.tankProduct} T{row.meta.tankNumber}
              </p>
            ) : (
              <p className="font-medium text-red-800">Row {row.lineNumber}</p>
            )}
          </div>
        </div>
        {!isValid && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 ml-2 text-red-400"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>
      {!isValid && expanded && (
        <ul className="mt-2 ml-6 space-y-0.5">
          {row.errors.map((e, i) => (
            <li key={i} className="text-xs text-red-600 list-disc">{e}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function BulkUploadModal({ branches, onAddEntry, onImported, onClose }) {
  const [step, setStep] = useState('upload')
  const [fileName, setFileName] = useState('')
  const [parseError, setParseError] = useState('')
  const [validRows, setValidRows] = useState([])
  const [errorRows, setErrorRows] = useState([])
  const [importResult, setImportResult] = useState(null)
  const [importing, setImporting] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  function processFile(file) {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please upload a .csv file.')
      return
    }
    setFileName(file.name)
    setParseError('')
    const reader = new FileReader()
    reader.onload = (e) => {
      const { rows, error } = parseCSV(e.target.result)
      if (error) {
        setParseError(error)
        return
      }
      const valid = []
      const errors = []
      rows.forEach((row) => {
        let result
        try {
          result = validateAndTransformRow(row, branches)
        } catch (err) {
          // A single malformed row must never kill the whole batch (previously an
          // unguarded throw here left the modal stuck on the upload step).
          result = { lineNumber: row.lineNumber, errors: [`Could not read this row: ${err?.message || err}`], tankId: null, entry: null, meta: row.raw }
        }
        if (result.errors.length === 0) valid.push(result)
        else errors.push(result)
      })
      setValidRows(valid)
      setErrorRows(errors)
      setStep('preview')
    }
    reader.readAsText(file)
  }

  function handleFileInput(e) {
    processFile(e.target.files[0])
  }

  function handleDrop(e) {
    e.preventDefault()
    setIsDragging(false)
    processFile(e.dataTransfer.files[0])
  }

  async function handleImport() {
    setImporting(true)
    let imported = 0
    let skipped = 0
    const skippedMessages = []
    for (const { tankId, entry, meta } of validRows) {
      let result
      try {
        result = await onAddEntry(tankId, entry)
      } catch (err) {
        result = { success: false, message: err?.message || 'Unexpected error' }
      }
      if (result?.success) {
        imported++
      } else {
        skipped++
        skippedMessages.push(`${entry.date} · ${meta.branchName} · ${meta.tankProduct}: ${result?.message || 'failed'}`)
      }
    }
    await onImported?.()
    setImporting(false)
    setImportResult({ imported, skipped, skippedMessages })
    setStep('done')
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 animate-fadeIn" onClick={onClose} />

      <div className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[70] animate-slideUp sm:animate-fadeIn">
        <div className="bg-white w-full sm:w-[560px] sm:max-h-[88vh] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl"
          style={{ maxHeight: '92vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="font-bold text-gray-800">Bulk Upload</h2>
              <p className="text-xs text-gray-400">Import daily entries from CSV</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">

            {/* STEP: UPLOAD */}
            {step === 'upload' && (
              <>
                {/* Drop zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-blue-400' : 'text-gray-300'}`} />
                  <p className="font-semibold text-gray-700 text-sm">
                    {fileName || 'Drop your CSV file here'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">or click to browse</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>

                {parseError && (
                  <p className="text-sm text-red-500 text-center">{parseError}</p>
                )}

                {/* Column guide */}
                <div className="bg-gray-50 rounded-xl p-3.5 space-y-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Required columns</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {['date (YYYY-MM-DD)', 'branchName', 'tankProduct', 'tankNumber',
                      'openingLevel', 'closingLevel', 'supplyReceived', 'pricePerLiter'].map((col) => (
                      <p key={col} className="text-xs text-gray-500 flex items-center space-x-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                        <span className="font-mono">{col}</span>
                      </p>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide pt-1">Optional columns</p>
                  <p className="text-xs text-gray-400 font-mono">
                    excessPer20L · pump1–4Opening/Closing · expense1–3Desc/Amount
                  </p>
                </div>
              </>
            )}

            {/* STEP: PREVIEW */}
            {step === 'preview' && (
              <>
                {/* Summary */}
                <div className="flex items-center space-x-2">
                  <span className="flex items-center space-x-1.5 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>{validRows.length} ready</span>
                  </span>
                  {errorRows.length > 0 && (
                    <span className="flex items-center space-x-1.5 bg-red-100 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{errorRows.length} errors</span>
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-1">{fileName}</span>
                </div>

                {/* Row list */}
                <div className="space-y-2">
                  {validRows.map((row, i) => <RowCard key={`v-${i}`} row={row} index={i} />)}
                  {errorRows.map((row, i) => <RowCard key={`e-${i}`} row={row} index={i} />)}
                </div>
              </>
            )}

            {/* STEP: DONE */}
            {step === 'done' && importResult && (
              <div className="py-6 text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Import Complete</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    <span className="font-semibold text-green-600">{importResult.imported}</span> rows imported successfully
                    {importResult.skipped > 0 && (
                      <>, <span className="font-semibold text-amber-500">{importResult.skipped}</span> skipped</>
                    )}
                  </p>
                </div>
                {importResult.skippedMessages.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-3 text-left space-y-1">
                    <p className="text-xs font-semibold text-amber-700">Skipped rows</p>
                    {importResult.skippedMessages.map((msg, i) => (
                      <p key={i} className="text-xs text-amber-600">{msg}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0"
            style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
          >
            {step === 'upload' && (
              <button
                onClick={() => triggerCSVDownload('petrotrack_template.csv', CSV_TEMPLATE)}
                className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 active:bg-gray-100 transition"
              >
                <Download className="w-4 h-4" />
                <span>Download CSV Template</span>
              </button>
            )}

            {step === 'preview' && (
              <div className="flex space-x-3">
                <button
                  onClick={() => { setStep('upload'); setValidRows([]); setErrorRows([]) }}
                  disabled={importing}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 transition"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={validRows.length === 0 || importing}
                  className="flex-1 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {importing ? 'Importing…' : `Import ${validRows.length > 0 ? `${validRows.length} rows` : ''}`}
                </button>
              </div>
            )}

            {step === 'done' && (
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 transition"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
